import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Network, Plus, Trash2, Building2, UserPlus, ArrowLeft, Loader2 } from "lucide-react";

export default function AdminDealerGroups() {
  const [groups, setGroups] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selected, setSelected] = useState(null); // group detail
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [addDealer, setAddDealer] = useState("");
  const [addLabel, setAddLabel] = useState("");
  const [accessEmail, setAccessEmail] = useState("");
  const [accessRole, setAccessRole] = useState("full");
  const [busy, setBusy] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([api.get("/admin/dealer-groups"), api.get("/admin/dealers")]);
      setGroups(g.data); setDealers(d.data);
    } catch { toast.error("Could not load dealer groups"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const openGroup = async (gid) => {
    try { const { data } = await api.get(`/admin/dealer-groups/${gid}`); setSelected(data); }
    catch { toast.error("Could not open group"); }
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/dealer-groups", { group_name: newName.trim() }); setNewName(""); await loadGroups(); openGroup(data.group_id); toast.success("Dealer group created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create group"); }
    finally { setBusy(false); }
  };

  const addMember = async () => {
    if (!addDealer) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/dealer-groups/${selected.group_id}/members`, { dealer_id: addDealer, location_label: addLabel || undefined }); setSelected(data); setAddDealer(""); setAddLabel(""); await loadGroups(); toast.success("Location added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add location"); }
    finally { setBusy(false); }
  };
  const removeMember = async (dealerId) => {
    try { const { data } = await api.delete(`/admin/dealer-groups/${selected.group_id}/members/${dealerId}`); setSelected(data); await loadGroups(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const grantAccess = async () => {
    if (!accessEmail.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/dealer-groups/${selected.group_id}/access`, { email: accessEmail.trim(), role: accessRole }); setSelected(data); setAccessEmail(""); toast.success("Executive access granted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not grant access"); }
    finally { setBusy(false); }
  };
  const revokeAccess = async (uid) => {
    try { const { data } = await api.delete(`/admin/dealer-groups/${selected.group_id}/access/${uid}`); setSelected(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  const memberIds = new Set((selected?.members || []).map((m) => m.dealer_id));
  const available = dealers.filter((d) => !memberIds.has(d.id));

  if (selected) {
    return (
      <div className="space-y-6 fade-up" data-testid="admin-dealer-group-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy" data-testid="group-back"><ArrowLeft className="w-4 h-4" /> All groups</button>
        <h1 className="text-3xl font-bold text-navy">{selected.group_name}</h1>

        <div className="bg-white rounded-2xl border border-border p-5">
          <h2 className="font-bold text-navy flex items-center gap-2 mb-4"><Building2 className="w-4 h-4 text-gold" /> Locations ({selected.member_count})</h2>
          <div className="divide-y divide-border" data-testid="group-members">
            {(selected.members || []).length === 0 ? <p className="text-sm text-muted-foreground">No locations yet. Add existing dealers below.</p> :
              selected.members.map((m) => (
                <div key={m.dealer_id} className="flex items-center justify-between py-2.5" data-testid={`group-member-${m.dealer_id}`}>
                  <div><div className="font-medium text-navy">{m.name}</div><div className="text-xs text-muted-foreground">{[m.city, m.state].filter(Boolean).join(", ")} {m.location_label ? `• ${m.location_label}` : ""}</div></div>
                  <button onClick={() => removeMember(m.dealer_id)} className="text-red-500 hover:text-red-700" data-testid={`remove-member-${m.dealer_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
          </div>
          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-border">
            <div className="min-w-[220px]">
              <label className="text-xs font-semibold text-muted-foreground">Add existing dealer</label>
              <Select value={addDealer} onValueChange={setAddDealer}>
                <SelectTrigger data-testid="add-member-select"><SelectValue placeholder="Select dealership" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">{available.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input value={addLabel} onChange={(e) => setAddLabel(e.target.value)} placeholder="Location label (optional)" className="w-56" data-testid="add-member-label" />
            <Button onClick={addMember} disabled={busy || !addDealer} className="bg-navy text-white" data-testid="add-member-btn"><Plus className="w-4 h-4 mr-1" /> Add location</Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border p-5">
          <h2 className="font-bold text-navy flex items-center gap-2 mb-4"><UserPlus className="w-4 h-4 text-gold" /> Executive Access</h2>
          <div className="divide-y divide-border" data-testid="group-access">
            {(selected.access || []).length === 0 ? <p className="text-sm text-muted-foreground">No executives assigned yet.</p> :
              selected.access.map((a) => (
                <div key={a.user_id} className="flex items-center justify-between py-2.5" data-testid={`group-access-${a.user_id}`}>
                  <div><div className="font-medium text-navy">{a.email}</div><div className="text-xs text-muted-foreground">{a.role === "full" ? "Group Executive Full Access" : "Group Executive View Only"}</div></div>
                  <button onClick={() => revokeAccess(a.user_id)} className="text-red-500 hover:text-red-700" data-testid={`revoke-access-${a.user_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
          </div>
          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-border">
            <div className="min-w-[240px]">
              <label className="text-xs font-semibold text-muted-foreground">Existing user email</label>
              <Input value={accessEmail} onChange={(e) => setAccessEmail(e.target.value)} placeholder="user@dealership.com" data-testid="access-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Role</label>
              <Select value={accessRole} onValueChange={setAccessRole}>
                <SelectTrigger className="w-56" data-testid="access-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="full">Group Executive Full Access</SelectItem>
                  <SelectItem value="view_only">Group Executive View Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={grantAccess} disabled={busy || !accessEmail} className="bg-navy text-white" data-testid="grant-access-btn"><UserPlus className="w-4 h-4 mr-1" /> Grant access</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up" data-testid="admin-dealer-groups">
      <div className="flex items-center gap-3"><Network className="w-7 h-7 text-gold" /><h1 className="text-3xl font-bold text-navy">Dealer Groups</h1></div>
      <p className="text-sm text-muted-foreground max-w-2xl">Group multiple dealership locations under one parent for Executive View. Each location stays its own dealer tenant with its own inventory, budget, and RecJet Mind decisions.</p>

      <div className="bg-white rounded-2xl border border-border p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs font-semibold text-muted-foreground">New group name</label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Black Label Marine Group" data-testid="new-group-name" />
        </div>
        <Button onClick={createGroup} disabled={busy || !newName.trim()} className="bg-gold text-navy font-semibold" data-testid="create-group-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create group</Button>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-navy"><tr className="text-left"><th className="px-4 py-3 font-semibold">Group</th><th className="px-4 py-3 font-semibold">Locations</th><th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-border" data-testid="groups-table">
            {loading ? <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Loading...</td></tr> :
              groups.length === 0 ? <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No dealer groups yet. Create one above.</td></tr> :
              groups.map((g) => (
                <tr key={g.group_id} className="hover:bg-secondary/40 cursor-pointer" onClick={() => openGroup(g.group_id)} data-testid={`group-row-${g.group_id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{g.group_name}</td>
                  <td className="px-4 py-3">{g.member_count}</td>
                  <td className="px-4 py-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{g.status}</span></td>
                  <td className="px-4 py-3 text-right"><span className="text-xs font-semibold text-primary">Manage</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
